import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import { openRazorpay } from '@/lib/razorpay';
import { toast } from 'sonner';
import { SHOP } from '@/constants/testIds';

const Checkout = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const { refresh: refreshCart } = useCart();
  const [cart, setCart] = useState(null);
  const [addr, setAddr] = useState({ full_name: user?.name || '', phone: '', line1: '', line2: '', city: '', state: '', pincode: '' });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    api.get('/shop/cart').then(({ data }) => setCart(data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!cart || cart.items.length === 0) { toast.error('Cart is empty'); return; }
    setProcessing(true);
    try {
      const { payment_id } = await openRazorpay({
        amount: cart.total,
        purpose: 'order',
        description: `Saans Ayurved — order (${cart.items.length} item${cart.items.length > 1 ? 's' : ''})`,
        name: addr.full_name || user?.name,
        email: user?.email,
        contact: addr.phone,
      });
      await api.post('/shop/checkout', { address: addr, payment_id });
      await refreshCart();
      toast.success('Order placed');
      nav('/dashboard', { replace: true });
    } catch (e) {
      if (e?.message === 'Payment cancelled') {
        toast.message('Payment cancelled');
      } else {
        toast.error(e?.response?.data?.detail || e?.message || 'Checkout failed');
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="bg-sans_bone">
      <section className="sans-section">
        <div className="sans-container grid md:grid-cols-12 gap-12">
          <form onSubmit={submit} className="md:col-span-8">
            <div className="sans-label mb-6">Checkout</div>
            <h1 className="sans-h1">Shipping address</h1>

            <div className="mt-12 grid sm:grid-cols-2 gap-8">
              {[
                ['full_name', 'Full name', 'sm:col-span-2'],
                ['phone', 'Phone', ''],
                ['line1', 'Address line 1', 'sm:col-span-2'],
                ['line2', 'Address line 2 (optional)', 'sm:col-span-2'],
                ['city', 'City', ''],
                ['state', 'State', ''],
                ['pincode', 'Pincode', ''],
              ].map(([key, label, span]) => (
                <div key={key} className={span}>
                  <label className="sans-label block mb-2">{label}</label>
                  <input
                    className="sans-input"
                    required={key !== 'line2'}
                    value={addr[key]}
                    onChange={(e) => setAddr({ ...addr, [key]: e.target.value })}
                  />
                </div>
              ))}
            </div>

            <div className="mt-10 p-4 bg-white border border-sans_line text-xs text-sans_mute leading-relaxed">
              <strong className="text-sans_ink">Secure checkout via Razorpay</strong> — UPI, cards, netbanking & wallets.
              Test mode: use card <code className="text-sans_ink">4111 1111 1111 1111</code>, any future expiry, any CVV.
            </div>

            <button
              type="submit"
              disabled={processing}
              data-testid={SHOP.placeOrderBtn}
              className="sans-btn-primary w-full sm:w-auto mt-10"
            >
              {processing ? 'Processing…' : `Place order — ₹${cart?.total ?? 0}`}
            </button>
          </form>

          <div className="md:col-span-4">
            <div className="border border-sans_line bg-white p-8 sticky top-24">
              <div className="sans-label mb-6">Your order</div>
              {cart?.items?.map((it) => (
                <div key={it.product_id} className="flex justify-between py-3 border-b border-sans_line last:border-0">
                  <div className="text-sm text-sans_ink">{it.name} × {it.quantity}</div>
                  <div className="text-sm text-sans_ink">₹{it.line_total}</div>
                </div>
              ))}
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-sans_mute">Subtotal</span><span className="text-sans_ink">₹{cart?.subtotal ?? 0}</span></div>
                <div className="flex justify-between"><span className="text-sans_mute">Shipping</span><span className="text-sans_ink">{cart?.shipping === 0 ? 'Free' : `₹${cart?.shipping}`}</span></div>
                <div className="border-t border-sans_line pt-2 flex justify-between font-serif text-lg"><span className="text-sans_ink">Total</span><span className="text-sans_ink">₹{cart?.total ?? 0}</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Checkout;
